import ProgressBar from "@ramonak/react-progress-bar";
import React from "react";
import { KJUUP_COLORS } from "../../helper/constants";
import LanguageContext from "../../helper/LanguageContext";
import {
  formatDocSize,
  getDocAccentColor,
  getDocExtension,
  getDocIconName,
  getDocMessageName,
} from "../../utils/documentAlbum";
import "../../styles/components.css";
import "./DocsSection.css";

const DocsSection = ({
  media,
  props,
  isSelect,
  setDoc,
  setViewDoc,
  _onCheckBoxPress,
  isChat,
  onOpenEmailItem,
  accentColor,
  downloadActive,
  selectionLocked,
  hideDateHeaders,
  hideEmpty,
  tiles,
}) => {
  const { strings } = React.useContext(LanguageContext);
  const accent =
    accentColor ||
    (props.isInternal ? KJUUP_COLORS.internal : KJUUP_COLORS.base_color);
  const hasAnyDocs = media?.some(
    (file) => file?.items?.filter((item) => item.messageType === "doc").length > 0
  );

  const openItem = (item, itemIndex, index) => {
    if (selectionLocked || item.isDownloading) return;
    if (isSelect) {
      _onCheckBoxPress(null, itemIndex, index);
      return;
    }
    if (item.source === "email") {
      if (onOpenEmailItem) onOpenEmailItem(item);
      return;
    }
    setDoc(item);
    setViewDoc(true);
  };

  // `tiles`: square cards in the photo grid (same size as the thumbnails) instead of list rows;
  // the full file name sits under the square.
  const renderTile = (item, itemIndex, index) => (
    <div
      key={item.id || itemIndex}
      className={`image-container docs-section__tile-item${item.isSelect ? " selected" : ""}${
        isSelect ? " select-mode" : ""
      }${downloadActive && !item.isSelect && !item.isDownloading ? " media-dimmed" : ""}`}
      onClick={() => openItem(item, itemIndex, index)}
    >
      <div
        className="image-wrapper docs-section__tile"
        style={{ "--docs-section-icon-color": getDocAccentColor(item) }}
      >
        <div className="docs-section__tile-body" title={getDocMessageName(item)}>
          <span className="material-symbols-outlined docs-section__tile-icon">
            {getDocIconName(item)}
          </span>
          <div className="docs-section__tile-meta">
            {[
              getDocExtension(item).toUpperCase(),
              formatDocSize(item?.fileSize || item?.size || 0),
            ]
              .filter(Boolean)
              .join(" · ")}
          </div>
        </div>
        {item.source === "email" && (
          <span
            title={strings.filter_email || "E-Mail"}
            className="docs-section__email-badge docs-section__tile-badge"
          >
            E-Mail
          </span>
        )}
        {item.source === "teams" && (
          <span
            title="Microsoft Teams"
            className="docs-section__email-badge docs-section__teams-badge docs-section__tile-badge"
          >
            Teams
          </span>
        )}
        {(isSelect || downloadActive) &&
          (item.isSelect || item.isDownloading || item.downloadDone) && (
          <div className="selection-overlay">
            {item.isDownloading ? (
              <div className="docs-section__progress">
                <ProgressBar
                  margin="5px"
                  bgColor={accent}
                  completed={item.progress}
                  height={"50%"}
                  width={"100%"}
                />
              </div>
            ) : item.downloadDone ? (
              <span className="material-symbols-outlined docs-section__done-icon docs-section__tile-done">
                check_circle
              </span>
            ) : null}
          </div>
        )}
        {isSelect && !item.isDownloading && !downloadActive && (
          <label
            className={props.isInternal ? "role-label-internal" : "role-label"}
            onClick={(e) => e.stopPropagation()}
          >
            <input
              onClick={(e) => e.stopPropagation()}
              onChange={() => _onCheckBoxPress(null, itemIndex, index)}
              value={item.isSelect}
              type="checkbox"
              checked={!!item.isSelect}
            />
            <span
              className={props.isInternal ? "checkmark-label-internal" : "checkmark-label"}
            />
          </label>
        )}
      </div>
      <div className="docs-section__tile-name">{getDocMessageName(item)}</div>
    </div>
  );

  if (!hasAnyDocs) {
    if (hideEmpty) return null;
    return (
      <div className="media-section-container media-empty-wrap">
        <div className="no-media" role="status">
          <span className="media-empty-label">
            {strings.no_documents_available || "No documents"}
          </span>
        </div>
      </div>
    );
  }

  return (
    <div
      className="media-section-container docs-section"
      style={{
        "--kjuup-brand": accent,
        "--kjuup-selection-border": accent,
      }}
    >
      <div className="docs-content mt-1">
        {media?.map((file, index) => (
          <React.Fragment key={`${file.createdAt}-${index}`}>
            {!hideDateHeaders &&
            file?.items?.length > 0 &&
            file?.items?.filter((item) => item.messageType === "doc").length >
              0 ? (
              <div
                className="date-header docs-section__date"
              >
                {file.createdAt}
              </div>
            ) : null}
            <div className={tiles ? "image-row" : "doc-row"}>
                {file?.items?.map(
                  (item, itemIndex) =>
                    item.messageType === "doc" && (tiles ? renderTile(item, itemIndex, index) : (
                      <div
                        key={item.id || itemIndex}
                        className={`doc-container docs-section__item${item.isSelect ? " selected docs-section__item--selected" : ""}${
                          downloadActive && !item.isSelect && !item.isDownloading
                            ? " media-dimmed"
                            : ""
                        }`}
                        onClick={() => openItem(item, itemIndex, index)}
                      >
                        <div className="doc-bubble doc-bubble--list">
                          <div
                            className="doc-bubble-icon docs-section__icon"
                            style={{
                              "--docs-section-icon-bg": `${getDocAccentColor(item)}1a`,
                              "--docs-section-icon-color": getDocAccentColor(item),
                            }}
                          >
                            <div className="material-symbols-outlined docs-section__icon-glyph">
                              {getDocIconName(item)}
                            </div>
                          </div>
                          <div className="doc-bubble-info">
                            <div className="doc-bubble-name" title={getDocMessageName(item)}>
                              {getDocMessageName(item)}
                            </div>
                            <div className="doc-bubble-meta">
                              {[
                                getDocExtension(item).toUpperCase(),
                                formatDocSize(item?.fileSize || item?.size || 0),
                              ]
                                .filter(Boolean)
                                .join(" · ")}
                            </div>
                          </div>
                          {item.source === "email" && (
                            <span
                              title={strings.filter_email || "E-Mail"}
                              className="docs-section__email-badge"
                            >
                              E-Mail
                            </span>
                          )}
                          {item.source === "teams" && (
                            <span
                              title="Microsoft Teams"
                              className="docs-section__email-badge docs-section__teams-badge"
                            >
                              Teams
                            </span>
                          )}
                          {isSelect && !item.isDownloading && !downloadActive && (
                            <label
                              className={`${
                                props.isInternal
                                  ? "doc-checkbox-label-internal"
                                  : "doc-checkbox-label"
                              } docs-section__checkbox`}
                              onClick={(e) => e.stopPropagation()}
                            >
                              <input
                                onClick={(e) => e.stopPropagation()}
                                onChange={() =>
                                  _onCheckBoxPress(null, itemIndex, index)
                                }
                                value={item.isSelect}
                                type="checkbox"
                                checked={!!item.isSelect}
                                className="d-none"
                              />
                              <span
                                className={
                                  props.isInternal
                                    ? "doc-checkmark-label-internal"
                                    : "doc-checkmark-label"
                                }
                              />
                            </label>
                          )}
                          {downloadActive && item.downloadDone && (
                            <span
                              className="material-symbols-outlined docs-section__done-icon"
                            >
                              check_circle
                            </span>
                          )}
                          {(isSelect || downloadActive) && item.isDownloading && (
                            <div className="docs-section__progress">
                              <ProgressBar
                                margin="5px"
                                bgColor={accent}
                                completed={item.progress}
                                height={"50%"}
                                width={"100%"}
                              />
                            </div>
                          )}
                        </div>
                      </div>
                    ))
                )}
            </div>
          </React.Fragment>
        ))}
      </div>
    </div>
  );
};

export default DocsSection;
